import { useLayoutEffect, useState, type RefObject } from 'react';
import { LOW_OPACITY } from '../bars/constants';

interface GetOpacityProps {
  currentItemKey: string;
  hoveredItemKey: string | null;
  isIncorrect?: boolean;
}

export const getOpacity = ({
  currentItemKey,
  hoveredItemKey,
  isIncorrect = false,
}: GetOpacityProps) => {
  if (hoveredItemKey === currentItemKey) {
    return 1;
  }
  if (hoveredItemKey !== null && hoveredItemKey !== currentItemKey) {
    return LOW_OPACITY;
  }
  if (isIncorrect) {
    return LOW_OPACITY;
  }
  return 1;
};

/**
 * Track the `width` of a resizable element using `ResizeObserver`.
 *
 * - When dealing with resized or animated elements, the ref's
 *   `clientWidth` property may not be updated fast enough during
 *   the render phase.
 * - `ResizeObserver` ensures we capture the `width` value immediately.
 *
 * @see https://linear.app/mentimeter/issue/PC-159/
 */
export const useElementWidth = (ref: RefObject<HTMLElement> | null): number => {
  const [width, setWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const container = ref?.current;
    if (!container) {
      return;
    }

    const updateWidth = () => {
      const width = container.clientWidth ?? 0;
      setWidth(width);
    };

    /**
     * Set initial width immediately.
     *
     * We're avoiding `useState(ref?.current?.clientWidth ?? 0)` because:
     * - During the initial render, the ref may not be attached yet
     *   (`ref.current` could be null).
     * - Even if attached, the element may not have its final layout
     *   dimensions yet during render phase
     * - ResizeObserver only fires when an element actually resizes,
     *   not on initial observation.
     */
    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return width;
};
